import React, { useState } from 'react';
import { Shield, Upload, CheckCircle, AlertCircle, User, Phone, FileText, ChevronRight } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const RISK_QUESTIONS = [
  {
    id: 'experience',
    question: 'What is your investment experience level?',
    options: ['Beginner (< 1 year)', 'Intermediate (1-5 years)', 'Experienced (5+ years)'],
  },
  {
    id: 'risk_tolerance',
    question: 'How would you describe your risk tolerance?',
    options: ['Conservative', 'Moderate', 'Aggressive'],
  },
  {
    id: 'investment_goal',
    question: 'What is your primary investment goal?',
    options: ['Capital preservation', 'Regular income', 'Long-term growth'],
  },
  {
    id: 'liquidity',
    question: 'How long can you keep funds invested?',
    options: ['Less than 1 year', '1-2 years', 'More than 2 years'],
  },
];

const KYCApplicationForm = ({ selectedTier, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    national_id: '',
    investment_amount: selectedTier?.minInvestment || 5000,
    tier: selectedTier?.id || 'starter',
    risk_answers: {},
    agreed_terms: false,
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRiskAnswer = (qId, answer) => {
    setForm((prev) => ({
      ...prev,
      risk_answers: { ...prev?.risk_answers, [qId]: answer },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data: profile, error: profileError } = await supabase
        ?.from('investor_profiles')
        ?.insert({
          user_id: user?.id || null,
          full_name: form?.full_name,
          email: form?.email,
          phone: form?.phone,
          national_id: form?.national_id,
          kyc_status: 'pending',
          risk_assessment: form?.risk_answers,
        })
        ?.select()
        ?.single();

      if (profileError) throw profileError;

      const tierRoi = { starter: 12, growth: 15, enterprise: 18 };
      const tierVehicles = { starter: 2, growth: 6, enterprise: 15 };

      const { error: investError } = await supabase
        ?.from('investments')
        ?.insert({
          investor_id: profile?.id,
          tier: form?.tier,
          amount: Number(form?.investment_amount),
          roi_percentage: tierRoi?.[form?.tier] || 12,
          vehicle_count: tierVehicles?.[form?.tier] || 2,
          status: 'pending',
          start_date: new Date()?.toISOString()?.split('T')?.[0],
        });

      if (investError) throw investError;

      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tierOptions = [
    { id: 'starter', label: 'Starter', min: 5000, roi: 12 },
    { id: 'growth', label: 'Growth', min: 15000, roi: 15 },
    { id: 'enterprise', label: 'Enterprise', min: 50000, roi: 18 },
  ];

  if (success) {
    return (
      <section id="apply" className="mb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={36} className="text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-[#1a2744] mb-3" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Application Submitted!
          </h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Your investment application is under review. Our team will verify your KYC documents and contact you within 2-3 business days.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-xs font-medium px-4 py-2 rounded-full">
            <Shield size={12} />
            KYC verification in progress
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" className="mb-16">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Investment Application
        </h2>
        <p className="text-slate-500 text-sm">Complete KYC verification to start investing</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {/* Step Indicator */}
        <div className="bg-slate-50 px-8 py-5 border-b border-slate-100">
          <div className="flex items-center gap-0">
            {['Personal Details', 'Investment Plan', 'Risk Assessment', 'Review & Submit']?.map((label, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step > i + 1
                        ? 'bg-green-500 text-white'
                        : step === i + 1
                        ? 'bg-[#1a2744] text-white'
                        : 'bg-slate-200 text-slate-400'
                    }`}
                  >
                    {step > i + 1 ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${step === i + 1 ? 'text-[#1a2744]' : 'text-slate-400'}`}>
                    {label}
                  </span>
                </div>
                {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > i + 1 ? 'bg-green-400' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-100">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Step 1: Personal Details */}
          {step === 1 && (
            <div className="space-y-5 max-w-lg">
              <h3 className="font-semibold text-[#1a2744] mb-4 flex items-center gap-2">
                <User size={16} className="text-[#c9a84c]" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block font-medium">Full Name *</label>
                  <input
                    type="text"
                    value={form?.full_name}
                    onChange={(e) => handleChange('full_name', e?.target?.value)}
                    placeholder="John Doe"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block font-medium">Email Address *</label>
                  <input
                    type="email"
                    value={form?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    placeholder="john@example.com"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block font-medium">Phone Number *</label>
                  <input
                    type="tel"
                    value={form?.phone}
                    onChange={(e) => handleChange('phone', e?.target?.value)}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block font-medium">National ID / Passport *</label>
                  <input
                    type="text"
                    value={form?.national_id}
                    onChange={(e) => handleChange('national_id', e?.target?.value)}
                    placeholder="ID Number"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                  />
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                <Upload size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-[#1a2744] mb-1">Document Upload</div>
                  <p className="text-xs text-slate-400">Upload your National ID, Passport, or KRA PIN certificate. Accepted: PDF, JPG, PNG (max 5MB)</p>
                  <button className="mt-2 text-xs text-[#c9a84c] font-medium hover:underline">
                    + Attach Documents
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Investment Plan */}
          {step === 2 && (
            <div className="max-w-lg space-y-5">
              <h3 className="font-semibold text-[#1a2744] mb-4 flex items-center gap-2">
                <FileText size={16} className="text-[#c9a84c]" />
                Investment Plan
              </h3>
              <div>
                <label className="text-xs text-slate-500 mb-2 block font-medium">Select Tier</label>
                <div className="space-y-2">
                  {tierOptions?.map((t) => (
                    <button
                      key={t?.id}
                      onClick={() => {
                        handleChange('tier', t?.id);
                        handleChange('investment_amount', t?.min);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left ${
                        form?.tier === t?.id
                          ? 'border-[#c9a84c] bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div>
                        <div className="font-semibold text-[#1a2744] text-sm">{t?.label}</div>
                        <div className="text-xs text-slate-400">Min: KES {t?.min?.toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#c9a84c] font-bold text-sm">{t?.roi}% ROI</div>
                        <div className="text-xs text-slate-400">per annum</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block font-medium">Investment Amount (KES)</label>
                <input
                  type="number"
                  value={form?.investment_amount}
                  onChange={(e) => handleChange('investment_amount', e?.target?.value)}
                  min={tierOptions?.find((t) => t?.id === form?.tier)?.min || 5000}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Minimum: KES {(tierOptions?.find((t) => t?.id === form?.tier)?.min || 5000)?.toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Risk Assessment */}
          {step === 3 && (
            <div className="max-w-lg space-y-6">
              <h3 className="font-semibold text-[#1a2744] mb-4 flex items-center gap-2">
                <Shield size={16} className="text-[#c9a84c]" />
                Risk Assessment Questionnaire
              </h3>
              {RISK_QUESTIONS?.map((q) => (
                <div key={q?.id}>
                  <p className="text-sm font-medium text-[#1a2744] mb-2">{q?.question}</p>
                  <div className="space-y-2">
                    {q?.options?.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleRiskAnswer(q?.id, opt)}
                        className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all text-sm ${
                          form?.risk_answers?.[q?.id] === opt
                            ? 'border-[#c9a84c] bg-amber-50 text-[#1a2744] font-medium'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="max-w-lg space-y-5">
              <h3 className="font-semibold text-[#1a2744] mb-4">Review Your Application</h3>
              <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                {[
                  { label: 'Full Name', value: form?.full_name },
                  { label: 'Email', value: form?.email },
                  { label: 'Phone', value: form?.phone },
                  { label: 'National ID', value: form?.national_id },
                  { label: 'Investment Tier', value: form?.tier?.charAt(0)?.toUpperCase() + form?.tier?.slice(1) },
                  { label: 'Investment Amount', value: `KES ${Number(form?.investment_amount)?.toLocaleString()}` },
                ]?.map((item) => (
                  <div key={item?.label} className="flex justify-between text-sm">
                    <span className="text-slate-400">{item?.label}</span>
                    <span className="font-medium text-[#1a2744]">{item?.value || '—'}</span>
                  </div>
                ))}
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form?.agreed_terms}
                  onChange={(e) => handleChange('agreed_terms', e?.target?.checked)}
                  className="mt-0.5 accent-[#c9a84c]"
                />
                <span className="text-xs text-slate-500">
                  I agree to the <span className="text-[#c9a84c] font-medium">Terms & Conditions</span> and{' '}
                  <span className="text-[#c9a84c] font-medium">Investment Risk Disclosure</span>. I confirm that all provided information is accurate.
                </span>
              </label>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-[#1a2744] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Back
            </button>
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-2 bg-[#1a2744] text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#0f1a35] transition-colors"
              >
                Continue <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !form?.agreed_terms}
                className="flex items-center gap-2 bg-[#c9a84c] text-[#1a2744] px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-[#b8973b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
                {!loading && <CheckCircle size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default KYCApplicationForm;
